// @ts-check
/**
 * <oc-product-compare> — the compare page (sections/oc-product-compare.liquid).
 *
 * Reads compare items from window.OcCompareStore (defined in assets/oc-compare.js),
 * builds N product header cells + N table cells per attribute row, then fetches
 * `/products/<handle>?sections=<adapter>` to pull full data + metafields and fill
 * each row's <td>.
 *
 * Behaviour:
 *   - Only as many product columns are rendered as there are items (no empty slots).
 *   - The remove (×) button works via event delegation so dynamically inserted
 *     header cells are handled without re-binding.
 *   - Listens to `oc:compare:change` so add/remove from another tab (or the remove
 *     buttons here) re-renders without a full page reload.
 */

const STORE_KEY = 'OcCompareStore';
const OC_COMPARE_CHANGE = 'oc:compare:change';

/**
 * @typedef {Object} OcCompareDisplayItem
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {string} url
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
function asText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (value);
    if ('value' in obj) return asText(obj.value);
    try { return JSON.stringify(value); } catch { return ''; }
  }
  return '';
}

/**
 * @param {string} html
 * @returns {string}
 */
function escapeHtml(html) {
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class OcProductCompare extends HTMLElement {
  /** @type {any} */
  store = null;
  /** @type {string} */
  adapterSection = 'oc_compare_data';
  /** @type {number} */
  max = 4;

  /** @type {HTMLElement | null} */
  emptyEl = null;
  /** @type {HTMLElement | null} */
  boardEl = null;
  /** @type {HTMLElement | null} */
  counterEl = null;
  /** @type {HTMLButtonElement | null} */
  clearBtn = null;
  /** @type {HTMLTableRowElement | null} */
  productsRow = null;
  /** @type {HTMLTableRowElement[]} */
  rowEls = [];
  /** @type {HTMLTemplateElement | null} */
  productTemplate = null;

  /** @type {Map<string, Promise<any>>} */
  fetchCache = new Map();
  /** @type {Map<string, any>} */
  dataCache = new Map();

  /** @type {() => void} */
  onChange = () => this.render();

  connectedCallback() {
    this.store = /** @type {any} */ (window)[STORE_KEY];
    if (!this.store) {
      console.warn('[oc-product-compare] OcCompareStore not found. Make sure oc-compare.js loads first.');
      return;
    }

    this.adapterSection = this.dataset.adapterSection || 'oc_compare_data';
    this.max = Number(this.dataset.max || 4);

    this.emptyEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-empty]'));
    this.boardEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-board]'));
    this.counterEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-counter]'));
    this.clearBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-clear]'));
    this.productsRow = /** @type {HTMLTableRowElement | null} */ (this.querySelector('[data-products-row]'));
    this.rowEls = /** @type {HTMLTableRowElement[]} */ (Array.from(this.querySelectorAll('[data-row]')));
    this.productTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('[data-product-template]'));

    window.addEventListener(OC_COMPARE_CHANGE, this.onChange);

    this.clearBtn?.addEventListener('click', () => this.store.clear());

    // Event delegation: handle remove (×), Add-to-cart and Buy-now clicks at the
    // products row level. Avoids re-binding when header cells are rebuilt every render.
    this.productsRow?.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      if (!target) return;

      const removeBtn = target.closest('[data-remove]');
      if (removeBtn) {
        e.preventDefault();
        const cell = removeBtn.closest('[data-product-cell]');
        const handle = cell?.getAttribute('data-handle');
        if (handle) this.store.remove(handle);
        return;
      }

      const minusBtn = /** @type {HTMLButtonElement | null} */ (target.closest('[data-qty-minus]'));
      if (minusBtn) {
        e.preventDefault();
        this.stepQuantity(minusBtn, -1);
        return;
      }
      const plusBtn = /** @type {HTMLButtonElement | null} */ (target.closest('[data-qty-plus]'));
      if (plusBtn) {
        e.preventDefault();
        this.stepQuantity(plusBtn, 1);
        return;
      }

      const atcBtn = /** @type {HTMLButtonElement | null} */ (target.closest('[data-atc]'));
      if (atcBtn) {
        e.preventDefault();
        this.handleAddToCart(atcBtn);
        return;
      }
    });

    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener(OC_COMPARE_CHANGE, this.onChange);
  }

  render() {
    /** @type {OcCompareDisplayItem[]} */
    const items = this.store.list().slice(0, this.max);
    const count = items.length;

    if (this.counterEl) {
      this.counterEl.textContent = `${count} of ${this.max}`;
      this.counterEl.hidden = count === 0;
    }
    if (this.clearBtn) this.clearBtn.hidden = count === 0;

    if (count === 0) {
      if (this.emptyEl) this.emptyEl.hidden = false;
      if (this.boardEl) this.boardEl.hidden = true;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.boardEl) this.boardEl.hidden = false;

    this.renderProductHeader(items);
    this.renderRows(items);

    items.forEach((item) => {
      this.loadProduct(item.handle).then((data) => {
        /** @type {OcCompareDisplayItem[]} */
        const currentItems = this.store.list().slice(0, this.max);
        const currentIndex = currentItems.findIndex((x) => x.handle === item.handle);
        if (currentIndex === -1) return;
        this.fillCellsFor(currentIndex, data);
        this.fillActionsFor(currentIndex, data);
      });
    });
  }

  /**
   * Reveals the ATC + quantity stepper in a product header cell and primes them
   * with the loaded variant id / availability. Hidden until data arrives.
   * @param {number} slotIndex
   * @param {any | null} data
   */
  fillActionsFor(slotIndex, data) {
    if (!this.productsRow) return;
    const cells = Array.from(this.productsRow.querySelectorAll('[data-product-cell]'));
    const cell = /** @type {HTMLElement | undefined} */ (cells[slotIndex]);
    if (!cell || !data) return;

    const actions = /** @type {HTMLElement | null} */ (cell.querySelector('[data-actions]'));
    const atc = /** @type {HTMLButtonElement | null} */ (cell.querySelector('[data-atc]'));
    const atcLabel = /** @type {HTMLElement | null} */ (cell.querySelector('[data-atc-label]'));
    const qtyInput = /** @type {HTMLInputElement | null} */ (cell.querySelector('[data-qty-input]'));
    const qtyMinus = /** @type {HTMLButtonElement | null} */ (cell.querySelector('[data-qty-minus]'));
    const qtyPlus = /** @type {HTMLButtonElement | null} */ (cell.querySelector('[data-qty-plus]'));

    if (!actions || !data.variant_id) return;

    cell.dataset.variantId = String(data.variant_id);
    actions.hidden = false;

    const available = data.variant_available !== undefined ? !!data.variant_available : !!data.available;
    if (atc) atc.disabled = !available;
    if (qtyInput) qtyInput.disabled = !available;
    if (qtyMinus) qtyMinus.disabled = !available;
    if (qtyPlus) qtyPlus.disabled = !available;
    if (atcLabel) atcLabel.textContent = available ? 'Add to cart' : 'Sold out';
  }

  /**
   * Bumps the quantity input next to the clicked +/− button by `delta`. Mimics
   * the theme's quantity-selector-component behaviour without depending on the
   * tightly-coupled product-form-component event bus.
   * @param {HTMLButtonElement} btn
   * @param {number} delta
   */
  stepQuantity(btn, delta) {
    const wrapper = btn.closest('[data-qty]');
    const input = /** @type {HTMLInputElement | null} */ (wrapper?.querySelector('[data-qty-input]') || null);
    if (!input) return;
    const min = Number(input.min || 1);
    const next = Math.max(min, (Number(input.value) || min) + delta);
    input.value = String(next);
  }

  /**
   * Posts to Shopify's cart-add endpoint for the variant + quantity tied to this
   * cell. Mirrors the Horizon product-form-component flow (FormData body,
   * `X-Requested-With: XMLHttpRequest`, includes the section ids that
   * <cart-items-component> elements ask for) so the theme's cart drawer/icon
   * receive a proper `cart:update` event and re-render. A raw JSON POST to
   * /cart/add.js succeeds at the API level but Horizon's cart UI never refreshes
   * because no CartAddEvent is dispatched.
   * @param {HTMLButtonElement} btn
   */
  handleAddToCart(btn) {
    const cell = /** @type {HTMLElement | null} */ (btn.closest('[data-product-cell]'));
    if (!cell) {
      console.warn('[oc-product-compare] ATC clicked but no [data-product-cell] ancestor found.');
      return;
    }
    const variantId = cell.dataset.variantId;
    if (!variantId) {
      console.warn('[oc-product-compare] ATC clicked but cell has no data-variant-id yet — product data may still be loading.');
      return;
    }
    const qtyInput = /** @type {HTMLInputElement | null} */ (cell.querySelector('[data-qty-input]'));
    const quantity = Math.max(1, Number((qtyInput && qtyInput.value) || 1));

    btn.dataset.loading = 'true';
    btn.disabled = true;
    this.showActionError(cell, '');

    const win = /** @type {any} */ (window);
    const cartAddUrl = (win.Theme && win.Theme.routes && win.Theme.routes.cart_add_url) || '/cart/add.js';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));

    // Ask cart-items-component sections to re-render so the cart drawer/page
    // updates in the same response — Horizon's product-form does the same.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter((id) => typeof id === 'string' && id.length > 0);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then((res) => res.json().then((json) => ({ ok: res.ok, status: res.status, json })))
      .then(({ ok, status, json }) => {
        // Shopify returns 4xx with { status, message, description } on validation errors.
        if (!ok || (json && json.status && json.status >= 400)) {
          throw new Error((json && (json.description || json.message)) || `Could not add to cart (HTTP ${status})`);
        }

        // Dispatch the same event Horizon's product-form-component dispatches so
        // <cart-icon-component>, <cart-drawer-component>, etc. pick up the change.
        this.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: undefined,
              sourceId: 'oc-product-compare',
              data: {
                source: 'oc-product-compare',
                productId: cell.dataset.handle,
                variantId,
                itemCount: quantity,
                sections: json && json.sections,
              },
            },
          })
        );

        const labelEl = /** @type {HTMLElement | null} */ (btn.querySelector('[data-atc-label]'));
        if (labelEl) {
          const original = labelEl.textContent;
          labelEl.textContent = 'Added ✓';
          setTimeout(() => {
            if (labelEl) labelEl.textContent = original || 'Add to cart';
          }, 1800);
        }
      })
      .catch((err) => {
        this.showActionError(cell, String((err && err.message) || err || 'Could not add to cart.'));
      })
      .finally(() => {
        delete btn.dataset.loading;
        const stillSoldOut = btn.textContent && /sold out|unavailable/i.test(btn.textContent);
        if (!stillSoldOut) btn.disabled = false;
      });
  }

  /**
   * @param {HTMLElement} cell
   * @param {string} message
   */
  showActionError(cell, message) {
    const errEl = /** @type {HTMLElement | null} */ (cell.querySelector('[data-action-error]'));
    if (!errEl) return;
    if (!message) {
      errEl.hidden = true;
      errEl.textContent = '';
      return;
    }
    errEl.textContent = message;
    errEl.hidden = false;
    setTimeout(() => {
      errEl.hidden = true;
    }, 4000);
  }

  /**
   * @param {OcCompareDisplayItem[]} items
   */
  renderProductHeader(items) {
    const row = this.productsRow;
    const tpl = this.productTemplate;
    if (!row || !tpl) return;

    Array.from(row.querySelectorAll('[data-product-cell]')).forEach((el) => el.remove());

    items.forEach((item) => {
      const frag = /** @type {DocumentFragment} */ (tpl.content.cloneNode(true));
      const cell = /** @type {HTMLElement | null} */ (frag.querySelector('[data-product-cell]'));
      if (!cell) return;
      cell.dataset.handle = item.handle;

      const link = /** @type {HTMLAnchorElement | null} */ (cell.querySelector('[data-link]'));
      const image = /** @type {HTMLImageElement | null} */ (cell.querySelector('[data-image]'));
      const title = /** @type {HTMLElement | null} */ (cell.querySelector('[data-title]'));
      if (link) link.href = item.url || '#';
      if (image) {
        image.src = item.image || '';
        image.alt = item.title || '';
      }
      if (title) title.textContent = item.title || '';

      row.appendChild(cell);
    });
  }

  /**
   * @param {OcCompareDisplayItem[]} items
   */
  renderRows(items) {
    this.rowEls.forEach((row) => {
      Array.from(row.querySelectorAll('[data-cell-slot]')).forEach((el) => el.remove());

      items.forEach((_, i) => {
        const td = document.createElement('td');
        td.className = 'oc-compare__cell';
        td.dataset.cellSlot = String(i);
        td.innerHTML = '<span class="oc-compare__cell-skeleton" data-skeleton></span>';
        row.appendChild(td);
      });
    });
  }

  /**
   * Loads product data with a two-tier strategy:
   *   1. Adapter section (`/products/<handle>?sections=<section>`) — gives us
   *      metafields + everything else.
   *   2. Fallback to `/products/<handle>.js` — works always but no metafields.
   * If the adapter is unreachable (404, not registered, returns empty) we still
   * populate every non-metafield row from the fallback so the UI isn't all dashes.
   * @param {string} handle
   * @returns {Promise<any | null>}
   */
  loadProduct(handle) {
    const cachedData = this.dataCache.get(handle);
    if (cachedData !== undefined) return Promise.resolve(cachedData);
    const cachedFetch = this.fetchCache.get(handle);
    if (cachedFetch) return cachedFetch;

    const promise = this.fetchAdapter(handle)
      .then(async (adapterData) => {
        if (adapterData) return adapterData;
        const fallback = await this.fetchFallback(handle);
        if (!fallback) {
          console.warn('[oc-product-compare] No data available for', handle, '(adapter + fallback both failed)');
        }
        return fallback;
      })
      .then((data) => {
        if (data) this.dataCache.set(handle, data);
        return data;
      })
      .finally(() => {
        this.fetchCache.delete(handle);
      });

    this.fetchCache.set(handle, promise);
    return promise;
  }

  /**
   * @param {string} handle
   * @returns {Promise<any | null>}
   */
  fetchAdapter(handle) {
    const section = this.adapterSection;
    const url = `/products/${encodeURIComponent(handle)}?sections=${encodeURIComponent(section)}`;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((payload) => {
        const html = payload && payload[section];
        if (typeof html !== 'string' || !html.trim()) {
          console.warn(
            '[oc-product-compare] Adapter section "' + section + '" missing in response for', handle,
            '— check that templates/product.json registers it and that sections/oc-compare-product-data.liquid exists. Falling back to /products/<handle>.js.'
          );
          return null;
        }
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const scriptEl = tmp.querySelector('script[data-oc-compare-product]');
        if (!scriptEl) {
          console.warn('[oc-product-compare] Adapter HTML had no <script data-oc-compare-product> for', handle);
          return null;
        }
        try {
          return JSON.parse(scriptEl.textContent || '{}');
        } catch (err) {
          console.warn('[oc-product-compare] Failed to parse adapter JSON for', handle, err, '\nRaw text:', scriptEl.textContent);
          return null;
        }
      })
      .catch((err) => {
        console.warn('[oc-product-compare] Adapter fetch failed for', handle, err);
        return null;
      });
  }

  /**
   * Fetches the standard Shopify product.js endpoint and maps it to the same
   * shape the adapter would have produced. Metafields are not available here.
   * @param {string} handle
   * @returns {Promise<any | null>}
   */
  fetchFallback(handle) {
    const url = `/products/${encodeURIComponent(handle)}.js`;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((p) => {
        if (!p) return null;
        const variant = (p.variants && p.variants[0]) || {};
        return {
          handle: p.handle,
          title: p.title,
          url: p.url,
          vendor: p.vendor,
          product_type: p.type,
          tags: p.tags,
          description: p.description,
          featured_image: p.featured_image,
          available: !!p.available,
          variant_id: variant.id,
          variant_available: variant.available != null ? !!variant.available : !!p.available,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          price_formatted: this.formatMoney(variant.price),
          compare_at_price_formatted: variant.compare_at_price != null ? this.formatMoney(variant.compare_at_price) : null,
          sku: variant.sku,
          options: (p.options || []).map((/** @type {any} */ opt, /** @type {number} */ idx) => {
            // /products/<handle>.js historically returned options as strings ["Color","Size"];
            // newer responses return objects [{name, position, values}, ...]. Handle both.
            const name = typeof opt === 'string' ? opt : (opt && opt.name) || '';
            const declaredValues = typeof opt === 'object' && Array.isArray(opt && opt.values) ? opt.values : null;
            const derivedValues = Array.from(new Set(
              (p.variants || []).map((/** @type {any} */ v) => v && v[`option${idx + 1}`]).filter(Boolean)
            ));
            return {
              name: String(name),
              values: (declaredValues && declaredValues.length ? declaredValues : derivedValues).map((/** @type {any} */ v) => String(v)),
              selected_value: variant[`option${idx + 1}`] || null,
            };
          }),
          metafields: { officechase: {} },
        };
      })
      .catch((err) => {
        console.warn('[oc-product-compare] Fallback /products/<handle>.js failed for', handle, err);
        return null;
      });
  }

  /**
   * Shopify's product.js returns price in cents (integer). Convert and format
   * with the user's locale; not perfect (no currency symbol awareness) but good
   * enough for the fallback when the adapter is unavailable.
   * @param {number | undefined | null} cents
   * @returns {string}
   */
  formatMoney(cents) {
    if (cents == null) return '';
    const value = Number(cents) / 100;
    try {
      const win = /** @type {any} */ (window);
      const currency = (win.Shopify && win.Shopify.currency && win.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  }

  /**
   * @param {number} slotIndex
   * @param {any | null} data
   */
  fillCellsFor(slotIndex, data) {
    this.rowEls.forEach((row) => {
      const cell = /** @type {HTMLElement | null} */ (row.querySelector(`[data-cell-slot="${slotIndex}"]`));
      if (!cell) return;
      // Per-row try/catch: a broken renderCell for one source (e.g. malformed
      // metafield value) must not stop the iteration and leave subsequent rows
      // stuck on the skeleton state.
      try {
        cell.innerHTML = this.renderCell(row, data);
      } catch (err) {
        console.warn('[oc-product-compare] renderCell failed for source', row.dataset.source, err);
        cell.innerHTML = '<span class="oc-compare__cell--muted">—</span>';
      }
    });
  }

  /**
   * @param {HTMLElement} row
   * @param {any | null} data
   * @returns {string}
   */
  renderCell(row, data) {
    if (!data) return '<span class="oc-compare__cell--muted">—</span>';

    const source = row.dataset.source || '';
    const htmlMode = row.dataset.htmlMode === 'true';

    switch (source) {
      case 'price': {
        const current = data.price_formatted || (data.price != null ? String(data.price) : '');
        const was = data.compare_at_price_formatted;
        if (was && data.compare_at_price && data.compare_at_price > data.price) {
          return `<span class="oc-compare__cell--price-was">${escapeHtml(was)}</span><span class="oc-compare__cell--price-current">${escapeHtml(current)}</span>`;
        }
        return `<span class="oc-compare__cell--price-current">${escapeHtml(current)}</span>`;
      }
      case 'availability': {
        return data.available
          ? '<span class="oc-compare__cell--available">In stock</span>'
          : '<span class="oc-compare__cell--unavailable">Out of stock</span>';
      }
      case 'vendor': return escapeHtml(data.vendor || '') || '<span class="oc-compare__cell--muted">—</span>';
      case 'product_type': return escapeHtml(data.product_type || '') || '<span class="oc-compare__cell--muted">—</span>';
      case 'sku': return escapeHtml(data.sku || '') || '<span class="oc-compare__cell--muted">—</span>';
      case 'tags': {
        const tags = Array.isArray(data.tags) ? data.tags : [];
        if (!tags.length) return '<span class="oc-compare__cell--muted">—</span>';
        return `<ul class="oc-compare__cell-list">${tags.map(
          (/** @type {unknown} */ t) => `<li>${escapeHtml(String(t))}</li>`
        ).join('')}</ul>`;
      }
      case 'options': {
        const rawOptions = Array.isArray(data.options) ? data.options : [];
        // Shopify products without variants come back as [{ name: "Title", values: ["Default Title"] }].
        // That isn't a real option from the merchant's POV — filter it out.
        const options = rawOptions.filter((/** @type {any} */ opt) => {
          if (!opt) return false;
          const name = String(opt.name || '');
          const vals = Array.isArray(opt.values) ? opt.values : [];
          if (name === 'Title' && vals.length <= 1 && (!vals[0] || String(vals[0]) === 'Default Title')) {
            return false;
          }
          return true;
        });
        if (!options.length) return '<span class="oc-compare__cell--muted">—</span>';
        return `<ul class="oc-compare__cell-list">${options.map(
          (/** @type {any} */ opt) => {
            const name = escapeHtml(String((opt && opt.name) || ''));
            const rawVals = opt && Array.isArray(opt.values) ? opt.values : [];
            const vals = rawVals.map((/** @type {unknown} */ v) => escapeHtml(String(v ?? ''))).join(', ');
            return `<li><strong>${name}:</strong> ${vals}</li>`;
          }
        ).join('')}</ul>`;
      }
      case 'description': {
        if (!data.description) return '<span class="oc-compare__cell--muted">—</span>';
        return htmlMode ? String(data.description) : escapeHtml(String(data.description));
      }
      case 'metafield': {
        const key = row.dataset.metafieldKey || '';
        const value = this.lookupMetafield(data, key);
        if (value === '' || value === null || value === undefined) {
          return '<span class="oc-compare__cell--muted">—</span>';
        }
        const text = asText(value);
        return htmlMode ? text : escapeHtml(text);
      }
      default:
        return '<span class="oc-compare__cell--muted">—</span>';
    }
  }

  /**
   * Look up "namespace.key" inside data.metafields. Supports both shapes:
   *   { officechase: { battery_life: "10h" } }
   *   { officechase: { battery_life: { value: "10h", type: "..." } } }
   * @param {any} data
   * @param {string} keyPath
   */
  lookupMetafield(data, keyPath) {
    if (!keyPath || !data || !data.metafields) return '';
    const dotIdx = keyPath.indexOf('.');
    if (dotIdx <= 0) return '';
    const namespace = keyPath.slice(0, dotIdx);
    const key = keyPath.slice(dotIdx + 1);
    const ns = data.metafields[namespace];
    if (!ns) return '';
    const raw = ns[key];
    if (raw === undefined || raw === null) return '';
    if (typeof raw === 'object' && 'value' in raw) return raw.value;
    return raw;
  }
}

if (!customElements.get('oc-product-compare')) {
  customElements.define('oc-product-compare', OcProductCompare);
}
